import "./App.css";
import React, { Suspense, lazy } from "react";
import { BrowserRouter as Router, Routes, Route, Navigate } from "react-router-dom";
import ErrorBoundary from "./components/shared/ErrorBoundary";
import LoadingState from "./components/shared/LoadingState";

// Public routes - loaded eagerly
import Login from "./components/shared/Login";
import PrivateRoute from "./components/shared/PrivateRoute";

// Lazy-loaded routes
const WelcomePage = lazy(() => import("./components/Commuter/WelcomePage"));
const Unauthorized = lazy(() => import("./components/shared/Unauthorized"));

// Admin
const AdminDashboard = lazy(() => import("./components/Admin/dashboard/AdminDashboard"));
const AdminCustomers = lazy(() => import("./components/Admin/customers/AdminCustomers"));
const AdminCustomerDetails = lazy(() => import("./components/Admin/customers/AdminCustomerDetails"));
const AdminTravelCardHolders = lazy(() => import("./components/Admin/travelCardHolders/AdminTravelCardHolders"));
const AdminTravelCardHolderDetails = lazy(() => import("./components/Admin/travelCardHolders/AdminTravelCardHolderDetails"));
const AdminPayments = lazy(() => import("./components/Admin/payments/AdminPayments"));
const AdminPaymentDetails = lazy(() => import("./components/Admin/payments/AdminPaymentDetails"));
const PreviousPayments = lazy(() => import("./components/Admin/payments/PreviousPayments"));
const AdminInvoices = lazy(() => import("./components/Admin/invoices/AdminInvoices"));
const AdminInvoiceDetails = lazy(() => import("./components/Admin/invoices/AdminInvoiceDetails"));
const PreviousInvoices = lazy(() => import("./components/Admin/invoices/PreviousInvoices"));
const AdminTellers = lazy(() => import("./components/Admin/tellers/AdminTellers"));
const AdminCollectionPoint = lazy(() => import("./components/Admin/tills/collection-points/AdminCollectionPoint"));
const CollectionPointDetails = lazy(() => import("./components/Admin/tills/collection-points/CollectionPointDetails"));
const AdminTillSessions = lazy(() => import("./components/Admin/tills/sessions/AdminTillSessions"));
const TillSessionDetails = lazy(() => import("./components/Admin/tills/sessions/TillSessionDetails"));
const AdminAccounting = lazy(() => import("./components/Admin/accounting/overview/AdminAccounting"));
const AdminAccounts = lazy(() => import("./components/Admin/accounting/coa/AdminAccounts"));
const AdminAccountDetails = lazy(() => import("./components/Admin/accounting/coa/AdminAccountDetails"));
const AdminJournals = lazy(() => import("./components/Admin/accounting/journals/AdminJournals"));
const AdminJournalDetails = lazy(() => import("./components/Admin/accounting/journals/AdminJournalDetails"));
const Banks = lazy(() => import("./components/Admin/accounting/banks/Banks"));
const BankDetails = lazy(() => import("./components/Admin/accounting/banks/BankDetails"));
const SalesSessions = lazy(() => import("./components/Admin/sales/sessions/SalesSessions"));
const PreviousSalesSessions = lazy(() => import("./components/Admin/sales/sessions/PreviousSalesSessions"));
const AdminOrders = lazy(() => import("./components/Admin/orders/AdminOrders"));
const AdminOrderDetails = lazy(() => import("./components/Admin/orders/AdminOrderDetails"));
const AdminProducts = lazy(() => import("./components/Admin/products/AdminProducts"));
const AdminProductDetails = lazy(() => import("./components/Admin/products/AdminProductDetails"));
const AdminTransporters = lazy(() => import("./components/Admin/transporters/AdminTransporters"));
const AdminTransporterDetails = lazy(() => import("./components/Admin/transporters/AdminTransporterDetails"));
const AdminBuses = lazy(() => import("./components/Admin/fleet/buses/AdminBuses"));
const AdminBusDetails = lazy(() => import("./components/Admin/fleet/buses/AdminBusDetails"));
const AdminRoutes = lazy(() => import("./components/Admin/fleet/bus-routes/AdminRoutes"));
const AdminRouteDetails = lazy(() => import("./components/Admin/fleet/bus-routes/AdminRouteDetails"));
const AdminBusDriver = lazy(() => import("./components/Admin/fleet/drivers/AdminBusDriver"));
const AdminDriverDetails = lazy(() => import("./components/Admin/fleet/drivers/AdminDriverDetails"));
const AdminValidators = lazy(() => import("./components/Admin/fleet/validators/AdminValidators"));
const BusRoster = lazy(() => import("./components/Admin/rosters/buses/BusRoster"));
const PreviousBusRoster = lazy(() => import("./components/Admin/rosters/buses/PreviousBusRoster"));
const ValidatorRoster = lazy(() => import("./components/Admin/rosters/validators/ValidatorRoster"));
const PreviousValidatorRoster = lazy(() => import("./components/Admin/rosters/validators/PreviousValidatorRoster"));
const DailyReports = lazy(() => import("./components/Admin/reports/daily/DailyReports"));
const WeeklyReports = lazy(() => import("./components/Admin/reports/weekly/WeeklyReports"));

// Super Agent
const TellerTill = lazy(() => import("./components/SuperAgent/Teller/TellerTill"));

// SLPTA
const SlptaDashboard = lazy(() => import("./components/SLPTA/SlptaDashboard"));
const SlptaReports = lazy(() => import("./components/SLPTA/reports/SlptaReports"));

// MTC
const MtcReports = lazy(() => import("./components/MTC/MtcReports"));
const MtcSchedule = lazy(() => import("./components/MTC/MtcSchedule"));

function App() {
  return (
    <ErrorBoundary>
      <Router>
        <Suspense fallback={<LoadingState message="Loading page..." />}>
          <Routes>
            {/* Public Routes */}
            <Route path="/login" element={<Login />} />
            <Route path="/commuter" element={<WelcomePage />} />
            <Route path="/unauthorized" element={<Unauthorized />} />
            <Route index element={<Navigate to="/login" replace />} />

            <Route element={<PrivateRoute />}>
              {/* Admin Routes */}
              <Route path="/admin" element={<AdminDashboard />} />

              <Route path="/customers" element={<AdminCustomers />} />
              <Route path="/customers/:customerId" element={<AdminCustomerDetails />} />

              <Route path="/travel-card-holders" element={<AdminTravelCardHolders />} />
              <Route path="/travel-card-holders/:travelCardHolderId" element={<AdminTravelCardHolderDetails />} />

              <Route path="/payments" element={<AdminPayments />} />
              <Route path="/payments/:paymentId" element={<AdminPaymentDetails />} />
              <Route path="/payments/previous-days" element={<PreviousPayments />} />

              <Route path="/invoices" element={<AdminInvoices />} />
              <Route path="/invoices/:invoiceId" element={<AdminInvoiceDetails />} />
              <Route path="/invoices/previous-days" element={<PreviousInvoices />} />

              <Route path="/tellers" element={<AdminTellers />} />

              <Route path="/tills/collection-points" element={<AdminCollectionPoint />} />
              <Route path="/tills/collection-points/:collectionPointId" element={<CollectionPointDetails />} />
              <Route path="/tills/sessions" element={<AdminTillSessions />} />
              <Route path="/tills/sessions/:tillSessionId" element={<TillSessionDetails />} />

              <Route path="/accounting" element={<AdminAccounting />} />
              <Route path="/accounting/charts-of-accounts" element={<AdminAccounts />} />
              <Route path="/accounting/charts-of-accounts/:accountId" element={<AdminAccountDetails />} />
              <Route path="/accounting/journals" element={<AdminJournals />} />
              <Route path="/accounting/journals/:journalId" element={<AdminJournalDetails />} />
              <Route path="/accounting/banks" element={<Banks />} />
              <Route path="/accounting/banks/:bankId" element={<BankDetails />} />

              <Route path="/sales/sessions" element={<SalesSessions />} />
              <Route path="/sales/sessions/previous-days" element={<PreviousSalesSessions />} />

              <Route path="/orders" element={<AdminOrders />} />
              <Route path="/orders/:orderId" element={<AdminOrderDetails />} />

              <Route path="/products" element={<AdminProducts />} />
              <Route path="/products/:productId" element={<AdminProductDetails />} />

              <Route path="/fleet/transporters" element={<AdminTransporters />} />
              <Route path="/fleet/transporters/:transporterId" element={<AdminTransporterDetails />} />
              <Route path="/fleet/buses" element={<AdminBuses />} />
              <Route path="/fleet/buses/:busId" element={<AdminBusDetails />} />
              <Route path="/fleet/routes" element={<AdminRoutes />} />
              <Route path="/fleet/routes/:routeId" element={<AdminRouteDetails />} />
              <Route path="/fleet/bus-drivers" element={<AdminBusDriver />} />
              <Route path="/fleet/bus-drivers/:driverId" element={<AdminDriverDetails />} />
              <Route path="/fleet/validators" element={<AdminValidators />} />

              <Route path="/rosters/buses" element={<BusRoster />} />
              <Route path="/rosters/buses/previous-days" element={<PreviousBusRoster />} />
              <Route path="/rosters/validators" element={<ValidatorRoster />} />
              <Route path="/rosters/validators/previous-days" element={<PreviousValidatorRoster />} />

              <Route path="/reports/daily" element={<DailyReports />} />
              <Route path="/reports/custom" element={<WeeklyReports />} />

              {/* Super Agent Manager Routes */}
              <Route path="/manager" element={<AdminDashboard />} />
              <Route path="/teller/till" element={<TellerTill />} />

              {/* Super Agent Sales Rep Routes */}
              <Route path="/sales-rep" element={<AdminInvoices />} />
              <Route path="/sales-rep/payments" element={<AdminPayments />} />

              {/* Operations Routes */}
              <Route path="/operations" element={<AdminBuses />} />

              {/* Ops Supervisor Routes */}
              <Route path="/ops-supervisor" element={<AdminDashboard />} />

              {/* SLPTA Routes */}
              <Route path="/slpta" element={<SlptaDashboard />} />
              <Route path="/slpta/reports" element={<SlptaReports />} />

              {/* MTC Routes */}
              <Route path="/mtc" element={<AdminBuses />} />
              <Route path="/mtc/buses" element={<AdminBuses />} />
              <Route path="/mtc/reports" element={<MtcReports />} />
              <Route path="/mtc/schedule" element={<MtcSchedule />} />

              <Route path="*" element={<Navigate to="/login" replace />} />
            </Route>
          </Routes>
        </Suspense>
      </Router>
    </ErrorBoundary>
  );
}

export default App;
